import { t } from 'i18next';
import { Edge, Node } from 'react-flow-renderer';

import {
  getCheckDisplayName,
  getCheckTypeIconName,
  GuardrailStageKey,
  isDemaskingEnabled,
  isMaskingEnabled,
  isSettingsEnabled,
} from '../../shared/utils';

import {
  defaultEdgeOptions,
  FLOW_ACTION_NODE_WIDTH,
  FLOW_DEMASKING_X_POSITION,
  FLOW_NODE_WIDTH,
  FLOW_STAGE_COMPACT_WIDTH,
  FLOW_STAGE_WIDTH,
} from './const';
import { GuardrailsFlowNodeData, IGuardrailsFlowChartProps, IFlowStageStep, StageStepsState } from './types';

const getStageTitle = (stageKey: GuardrailStageKey): string =>
  stageKey === GuardrailStageKey.Output
    ? t('Agent: Guardrails: Stage: Output: Title')
    : t('Agent: Guardrails: Stage: Input: Title');

const buildFlowStageSteps = (stageSteps: StageStepsState, stageKey: GuardrailStageKey): IFlowStageStep[] =>
  stageSteps[stageKey].map((step) => ({
    enabled: isSettingsEnabled(step.check.state),
    iconName: getCheckTypeIconName(step.check.type),
    key: step.key,
    title: getCheckDisplayName(step.check),
  }));

interface IBuildFlowNodesOptions extends IGuardrailsFlowChartProps {
  isCompactFlowChart: boolean;
}

const FLOW_LAYOUT_COLUMN_X = {
  message: 0,
  primaryAction: 175,
  inputStage: 385,
  demasking: FLOW_DEMASKING_X_POSITION,
} as const;

const FLOW_LAYOUT_ROW_Y = {
  userMessage: 0,
  inputStage: 10,
  llmCall: 188,
  outputStage: 198,
  demasking: 260,
  llmResponse: 376,
} as const;

// Centers the masking block relative to the input stage after border sub-pixel rendering.
const FLOW_MASKING_SUBPIXEL_ALIGNED_Y = 48.9;
const FLOW_DEMASKING_NODE_WIDTH = 160;

export const buildFlowNodes = ({
  isCompactFlowChart,
  masking,
  onAddStep,
  saving,
  stageSteps,
}: IBuildFlowNodesOptions): Node<GuardrailsFlowNodeData>[] => {
  const maskingEnabled = isMaskingEnabled(masking);
  const demaskingEnabled = isDemaskingEnabled(masking);
  const stageWidth = isCompactFlowChart ? FLOW_STAGE_COMPACT_WIDTH : FLOW_STAGE_WIDTH;
  const demaskingXPosition = FLOW_DEMASKING_X_POSITION - (FLOW_STAGE_WIDTH - stageWidth);

  return [
    {
      id: 'user-message',
      type: 'base',
      position: { x: FLOW_LAYOUT_COLUMN_X.message, y: FLOW_LAYOUT_ROW_Y.userMessage },
      style: { pointerEvents: 'all', width: FLOW_NODE_WIDTH },
      data: { kind: 'message', title: t('Agent: Guardrails: Flow: UserMessage') },
      draggable: false,
      selectable: false,
    },
    {
      id: 'masking',
      type: 'base',
      position: { x: FLOW_LAYOUT_COLUMN_X.primaryAction, y: FLOW_MASKING_SUBPIXEL_ALIGNED_Y },
      style: { pointerEvents: 'all', width: FLOW_ACTION_NODE_WIDTH },
      data: {
        disabled: !maskingEnabled,
        kind: 'action',
        title: t('Agent: Guardrails: Masking: StaticCard: Title'),
      },
      draggable: false,
      selectable: false,
    },
    {
      id: 'input-stage',
      type: 'stage',
      position: { x: FLOW_LAYOUT_COLUMN_X.inputStage, y: FLOW_LAYOUT_ROW_Y.inputStage },
      style: { pointerEvents: 'all', width: stageWidth },
      data: {
        addLabel: t('Agent: Guardrails: Steps: Add'),
        disabled: saving,
        emptyLabel: t('Agent: Guardrails: Steps: Empty'),
        onAddClick: () => onAddStep(GuardrailStageKey.Input),
        steps: buildFlowStageSteps(stageSteps, GuardrailStageKey.Input),
        title: getStageTitle(GuardrailStageKey.Input),
      },
      draggable: false,
      selectable: false,
    },
    {
      id: 'llm-call',
      type: 'base',
      position: { x: FLOW_LAYOUT_COLUMN_X.message, y: FLOW_LAYOUT_ROW_Y.llmCall },
      style: { pointerEvents: 'all', width: FLOW_NODE_WIDTH },
      data: { kind: 'message', title: t('Agent: Guardrails: Flow: LlmCall') },
      draggable: false,
      selectable: false,
    },
    {
      id: 'output-stage',
      type: 'stage',
      position: { x: FLOW_LAYOUT_COLUMN_X.primaryAction, y: FLOW_LAYOUT_ROW_Y.outputStage },
      style: { pointerEvents: 'all', width: stageWidth },
      data: {
        addLabel: t('Agent: Guardrails: Steps: Add'),
        disabled: saving,
        emptyLabel: t('Agent: Guardrails: Steps: Empty'),
        onAddClick: () => onAddStep(GuardrailStageKey.Output),
        steps: buildFlowStageSteps(stageSteps, GuardrailStageKey.Output),
        title: getStageTitle(GuardrailStageKey.Output),
      },
      draggable: false,
      selectable: false,
    },
    {
      id: 'demasking',
      type: 'base',
      position: { x: demaskingXPosition, y: FLOW_LAYOUT_ROW_Y.demasking },
      style: { pointerEvents: 'all', width: FLOW_DEMASKING_NODE_WIDTH },
      data: {
        disabled: !demaskingEnabled,
        kind: 'action',
        title: t('Agent: Guardrails: Demasking: StaticCard: Title'),
      },
      draggable: false,
      selectable: false,
    },
    {
      id: 'llm-response',
      type: 'base',
      position: { x: FLOW_LAYOUT_COLUMN_X.message, y: FLOW_LAYOUT_ROW_Y.llmResponse },
      style: { pointerEvents: 'all', width: FLOW_NODE_WIDTH },
      data: { kind: 'message', title: t('Agent: Guardrails: Flow: LlmResponse') },
      draggable: false,
      selectable: false,
    },
  ];
};

export const flowEdges: Edge[] = [
  {
    ...defaultEdgeOptions,
    id: 'user-message-masking',
    source: 'user-message',
    sourceHandle: 'right',
    target: 'masking',
    targetHandle: 'left',
  },
  {
    ...defaultEdgeOptions,
    id: 'masking-input-stage',
    source: 'masking',
    sourceHandle: 'right',
    target: 'input-stage',
    targetHandle: 'left',
  },
  {
    ...defaultEdgeOptions,
    id: 'input-stage-llm-call',
    source: 'input-stage',
    sourceHandle: 'bottom',
    target: 'llm-call',
    targetHandle: 'top',
  },
  {
    ...defaultEdgeOptions,
    id: 'llm-call-output-stage',
    source: 'llm-call',
    sourceHandle: 'right',
    target: 'output-stage',
    targetHandle: 'left',
  },
  {
    ...defaultEdgeOptions,
    id: 'output-stage-demasking',
    source: 'output-stage',
    sourceHandle: 'right',
    target: 'demasking',
    targetHandle: 'left',
  },
  {
    ...defaultEdgeOptions,
    id: 'demasking-llm-response',
    source: 'demasking',
    sourceHandle: 'bottom',
    target: 'llm-response',
    targetHandle: 'top',
  },
];
